import React from "react";
import { Link } from "react-router-dom";
import { Phone, Star, ShieldCheck, Clock, BadgeCheck, ArrowRight, MapPin, Plane, Briefcase, Users, Wine, Building2 } from "lucide-react";
import { SITE, SERVICE_AREAS } from "../data/site";
import { SERVICES } from "../data/services";
import { FLEET } from "../data/fleet";
import { TESTIMONIALS } from "../data/testimonials";
import BookingForm from "../components/BookingForm";
import ServiceCard from "../components/ServiceCard";
import HeroSlider from "../components/HeroSlider";

const TRUST = [
  { icon: BadgeCheck, label: "Serving DMV Since 1984" },
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Briefcase, label: "Corporate Accounts" },
  { icon: Clock, label: "24/7 Dispatch" },
];

export default function Home() {
  return (
    <div className="bg-[#050505]">
      {/* HERO */}
      <section className="relative min-h-[78vh] md:min-h-[92vh] flex items-center pt-24 md:pt-28 pb-14 md:pb-20 overflow-hidden" data-testid="hero">
        <HeroSlider />

        <div className="relative z-[5] max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-8 lg:gap-12 w-full">
          <div className="lg:col-span-7 fade-up">
            <div className="label-eyebrow text-[10px] md:text-xs mb-3 md:mb-5">USA Limo Service · Est. 1984</div>
            <h1 className="font-display text-[2.5rem] leading-[1.02] sm:text-6xl lg:text-7xl text-white tracking-tight">
              Luxury Transportation<br />
              <span className="text-white/90">Since</span> <span className="text-[#d4af6a]">1984</span>
            </h1>
            <p className="mt-5 md:mt-6 text-[15px] sm:text-lg text-white/80 max-w-2xl leading-relaxed">
              Professional chauffeur and luxury transportation throughout
              <span className="text-white"> Washington DC, Virginia & Maryland</span>.
              Trusted by executives and embassies for over four decades.
            </p>

            <div className="mt-7 md:mt-8 flex flex-wrap gap-2.5 md:gap-3">
              <Link to="/book" className="btn-accent" data-testid="hero-book">
                Book Online <ArrowRight size={16} />
              </Link>
              <a href={`tel:${SITE.phonesTel.virginia}`} className="btn-outline" data-testid="hero-call">
                <Phone size={14} /> <span className="hidden sm:inline">Call </span>{SITE.phones.virginia}
              </a>
              <Link to="/contact" className="btn-outline hidden sm:inline-flex" data-testid="hero-quote">Get A Quote</Link>
            </div>

            <div className="mt-8 md:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 max-w-3xl">
              {TRUST.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-white/85">
                  <Icon size={16} className="text-[#d4af6a] flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs tracking-wider uppercase leading-tight">{label}</span>
                </div>
              ))}
            </div>

            {/* Phone numbers — desktop row */}
            <div className="hidden sm:flex mt-10 flex-wrap gap-x-6 gap-y-3 text-sm">
              <a href={`tel:${SITE.phonesTel.office}`} className="text-white/75 hover:text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/45 mr-2">Office</span>
                {SITE.phones.office}
              </a>
              <a href={`tel:${SITE.phonesTel.virginia}`} className="text-white/75 hover:text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/45 mr-2">Virginia</span>
                {SITE.phones.virginia}
              </a>
              <a href={`tel:${SITE.phonesTel.dispatch}`} className="text-white/75 hover:text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/45 mr-2">Dispatch · 24/7</span>
                {SITE.phones.dispatch}
              </a>
            </div>

            {/* Phone numbers — mobile mini-cards */}
            <div className="sm:hidden mt-8 grid grid-cols-1 gap-2.5" data-testid="hero-mobile-phones">
              <a
                href={`tel:${SITE.phonesTel.office}`}
                className="flex items-center justify-between gap-3 px-4 py-3 bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
                data-testid="hero-phone-office"
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center bg-white/[0.05] border border-white/10">
                    <Phone size={14} className="text-[#d4af6a]" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-[9px] tracking-[0.3em] uppercase text-white/45">Office</span>
                    <span className="text-white text-sm font-medium">{SITE.phones.office}</span>
                  </span>
                </span>
                <ArrowRight size={14} className="text-white/30" />
              </a>
              <a
                href={`tel:${SITE.phonesTel.virginia}`}
                className="flex items-center justify-between gap-3 px-4 py-3 bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
                data-testid="hero-phone-virginia"
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center bg-white/[0.05] border border-white/10">
                    <Phone size={14} className="text-[#d4af6a]" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-[9px] tracking-[0.3em] uppercase text-white/45">Virginia</span>
                    <span className="text-white text-sm font-medium">{SITE.phones.virginia}</span>
                  </span>
                </span>
                <ArrowRight size={14} className="text-white/30" />
              </a>
              <a
                href={`tel:${SITE.phonesTel.dispatch}`}
                className="flex items-center justify-between gap-3 px-4 py-3 bg-[#9B1B30]/10 border border-[#9B1B30]/35 backdrop-blur-sm hover:bg-[#9B1B30]/15 transition-colors"
                data-testid="hero-phone-dispatch"
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center bg-[#9B1B30]/20 border border-[#9B1B30]/40 relative">
                    <Phone size={14} className="text-[#9B1B30]" />
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#9B1B30] pulse-red" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-[9px] tracking-[0.3em] uppercase text-[#9B1B30]/90 font-semibold">Dispatch · 24/7</span>
                    <span className="text-white text-sm font-medium">{SITE.phones.dispatch}</span>
                  </span>
                </span>
                <ArrowRight size={14} className="text-[#9B1B30]/70" />
              </a>
            </div>

            {/* Mobile-only quick book/quote teaser strip below hero content */}
            <div className="sm:hidden mt-8 flex items-center gap-3 text-white/65 text-xs">
              <span className="h-px flex-1 bg-white/10" />
              <span className="tracking-[0.3em] uppercase">Scroll for reservation</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
          </div>

          {/* Booking form — hidden on mobile (shown as dedicated section below for cleaner mobile hero) */}
          <div className="hidden lg:block lg:col-span-5 fade-up d-2">
            <BookingForm compact />
          </div>
        </div>
      </section>

      {/* MOBILE BOOKING FORM — own section, premium card */}
      <section className="lg:hidden py-12 bg-[#0a0a0a] border-y border-white/5" data-testid="mobile-book-section">
        <div className="px-5 max-w-2xl mx-auto">
          <BookingForm compact />
        </div>
      </section>

      {/* ABOUT / INTRO STRIP */}
      <section className="py-24 md:py-28 border-t border-white/5" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-4">About USA Limo Service</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight leading-tight">
              The DMV's most trusted name in luxury transportation — for forty years.
            </h2>
          </div>
          <div className="lg:col-span-7 text-white/70 leading-relaxed space-y-5 text-[15px]">
            <p>
              Since 1984, <strong className="text-white">USA Limo Service</strong> has been the preferred
              luxury transportation company across Washington DC, Northern Virginia, and Maryland.
              From <strong className="text-white">Dulles, Reagan and BWI airport transfers</strong> to
              executive corporate travel, VIP events, and embassy-grade chauffeured service, we deliver
              an experience built on punctuality, discretion, and uncompromising standards.
            </p>
            <p>
              Our pristine fleet of <strong className="text-white">executive sedans, Cadillac Escalades,
              Chevrolet Suburbans, Lincoln Navigators, GMC Yukons, Mercedes-Benz S-Class sedans, Sprinter
              vans, motor coaches and stretch limousines</strong> is maintained to flagship standards.
              Every chauffeur is professionally trained, background-checked, and held to the
              service expectations of our most discerning clients — Fortune 500 executives,
              law firms, government contractors, embassies, and luxury travelers.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-3">
              {["Professional Chauffeurs", "Luxury Fleet", "Executive Service", "Airport Transportation", "Corporate Travel", "VIP Events"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-white/80">
                  <span className="w-1.5 h-1.5 bg-[#9B1B30]" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="label-eyebrow mb-3">Our Luxury Services</div>
              <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight">
                A complete suite of executive transportation.
              </h2>
            </div>
            <Link to="/services" className="btn-outline" data-testid="all-services-link">
              View All Services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>

          {/* Highlighted dual CTA: Executive Chauffeur + Zuber Valet */}
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Link to="/executive-chauffeur" className="lux-card p-8 flex items-center gap-6 group" data-testid="cta-exec-chauffeur">
              <Briefcase size={32} className="text-[#d4af6a] flex-shrink-0" />
              <div className="flex-1">
                <div className="label-eyebrow mb-1.5">Executive Chauffeur For Hire</div>
                <div className="font-display text-2xl text-white">We Drive Your Vehicle — You Relax</div>
                <p className="text-sm text-white/60 mt-2">Professional chauffeurs in your own car. Privacy, convenience, luxury.</p>
              </div>
              <ArrowRight size={20} className="text-white/40 group-hover:text-[#9B1B30] transition-colors" />
            </Link>
            <Link to="/zuber-valet" className="lux-card p-8 flex items-center gap-6 group" data-testid="cta-zuber">
              <img src={SITE.zuberLogo} alt="Zuber Valet" className="w-16 h-16 bg-white p-2 flex-shrink-0 object-contain" />
              <div className="flex-1">
                <div className="label-eyebrow mb-1.5">Zuber Valet Service</div>
                <div className="font-display text-2xl text-white">Luxury Valet for Premium Events</div>
                <p className="text-sm text-white/60 mt-2">Professional parking, first-class arrival, reliable valet across the DMV.</p>
              </div>
              <ArrowRight size={20} className="text-white/40 group-hover:text-[#9B1B30] transition-colors" />
            </Link>
          </div>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="py-24 md:py-32" data-testid="fleet-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="label-eyebrow mb-3">Our Luxury Fleet</div>
              <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight max-w-2xl">
                From executive sedans to motor coaches — maintained to flagship standards.
              </h2>
            </div>
            <Link to="/fleet" className="btn-outline" data-testid="all-fleet-link">
              Explore Fleet <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FLEET.slice(0, 4).map((v) => (
              <div key={v.name} className="lux-card overflow-hidden" data-testid={`fleet-card-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="h-48 bg-black overflow-hidden">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h4 className="font-display text-lg text-white">{v.name}</h4>
                  <div className="text-xs text-white/50 mt-1">{v.capacity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        className="py-24 md:py-32 relative bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(5,5,5,0.85), rgba(5,5,5,0.92)), url(${SITE.jefferson})` }}
        data-testid="why-us"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="label-eyebrow mb-3">Why USA Limo Service</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight">
              Forty years of luxury, on time, every time.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Since 1984", desc: "Four decades of trusted luxury service across the DMV." },
              { title: "Executive Chauffeurs", desc: "Professionally trained, background-checked, discreet." },
              { title: "Pristine Luxury Fleet", desc: "Cadillac, Lincoln, Mercedes-Benz, Sprinter, motor coach." },
              { title: "24/7 Reliable Dispatch", desc: "Live human dispatch, always — never an automated line." },
              { title: "Corporate Transportation Experts", desc: "Embassy, law firm, government contractor accounts." },
              { title: "Nationwide Capability", desc: "DC, VA, MD service plus nationwide coordination." },
            ].map((f) => (
              <div key={f.title} className="lux-card p-7" data-testid={`why-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <Star size={18} className="text-[#d4af6a] mb-3" />
                <h4 className="font-display text-xl text-white mb-2">{f.title}</h4>
                <p className="text-sm text-white/65 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6">
          <div className="label-eyebrow mb-3">Client Stories</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight mb-14 max-w-3xl">
            Trusted by executives, embassies, and discerning travelers.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t) => (
              <blockquote key={t.name} className="lux-card p-8" data-testid={`testimonial-${t.name.replace(/\s+/g, "-")}`}>
                <div className="flex gap-1 mb-4 text-[#d4af6a]">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
                <p className="font-display text-lg text-white/90 leading-relaxed italic">"{t.quote}"</p>
                <footer className="mt-5 text-sm">
                  <div className="text-white">{t.name}</div>
                  <div className="text-white/50 text-xs tracking-wider uppercase mt-0.5">{t.role}</div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS / SEO BLOCK */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="service-areas">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Service Areas</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
              Premier luxury transportation across the DMV.
            </h2>
            <p className="text-white/65 mt-5 leading-relaxed">
              USA Limo Service is the preferred <strong className="text-white">limo service in Washington DC</strong>,
              the leading <strong className="text-white">chauffeur service in Virginia</strong>, and a trusted
              <strong className="text-white"> black car service across Maryland</strong>. Whether you need
              <strong className="text-white"> airport transportation to Dulles (IAD), Reagan (DCA) or BWI</strong>,
              or a <strong className="text-white">wedding limo in the DMV</strong>, our team delivers
              executive-class service on every ride.
            </p>
            <Link to="/contact" className="btn-outline mt-7 inline-flex" data-testid="areas-contact">
              <MapPin size={14} /> Get In Touch
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SERVICE_AREAS.map((a) => (
              <div key={a} className="lux-card px-5 py-4 text-sm text-white/80 flex items-center gap-2" data-testid={`area-${a.replace(/\s+/g, "-").toLowerCase()}`}>
                <MapPin size={14} className="text-[#9B1B30]" /> {a}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO RICH CONTENT BLOCK */}
      <section className="py-20 bg-[#080808] border-t border-white/5" data-testid="seo-content">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-display text-2xl text-white mb-4">Washington DC Limo & Black Car Service</h3>
            <p className="text-white/65 text-[15px] leading-relaxed">
              When you need <strong className="text-white">luxury transportation in Washington DC</strong>, USA Limo Service
              has been the trusted choice since 1984. We provide executive sedans, SUVs, sprinter vans, and stretch
              limousines for corporate clients, government officials, law firms, and embassies throughout the
              capital region.
            </p>
            <h3 className="font-display text-2xl text-white mt-8 mb-4">Northern Virginia Chauffeur Service</h3>
            <p className="text-white/65 text-[15px] leading-relaxed">
              From <strong className="text-white">Arlington, Alexandria, McLean, Tysons and Fairfax</strong> to Reston and Loudoun
              wine country, our Virginia chauffeur service delivers consistency, punctuality, and luxury.
              Our drivers know every shortcut between Dulles International Airport, Reagan National, and the
              region's top venues.
            </p>
          </div>
          <div>
            <h3 className="font-display text-2xl text-white mb-4">Maryland Luxury Car Service</h3>
            <p className="text-white/65 text-[15px] leading-relaxed">
              <strong className="text-white">Bethesda, Rockville, Silver Spring, Chevy Chase and beyond</strong> — USA Limo
              Service is your premier Maryland luxury car service. We specialize in BWI airport transfers,
              corporate accounts, and event transportation for the Maryland medical and academic communities.
            </p>
            <h3 className="font-display text-2xl text-white mt-8 mb-4">Airport Transportation · IAD · DCA · BWI</h3>
            <p className="text-white/65 text-[15px] leading-relaxed">
              Flight tracking, executive meet & greet, and on-time guarantees at all three major DMV airports.
              We monitor your flight in real time, adjust to delays, and have your chauffeur and luxury vehicle
              waiting curbside or at private jet terminals.
            </p>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 bg-[#9B1B30]" data-testid="cta-strip">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="label-eyebrow text-white/70 mb-3">Need Luxury Transportation?</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight mb-7">
            Call today or reserve online — 24/7.
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book" className="btn-outline bg-white text-black border-white hover:bg-white/90" data-testid="cta-book">
              Book Online
            </Link>
            <a href={`tel:${SITE.phonesTel.dispatch}`} className="btn-outline border-white hover:bg-white hover:text-[#9B1B30]" data-testid="cta-call-dispatch">
              <Phone size={14} /> Dispatch: {SITE.phones.dispatch}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
